/**
 * Shared Formatters - Cross-Platform Utility Functions
 * 
 * Consolidates formatting functions used across web, iOS, and Android.
 * These were previously duplicated inline across 15+ files.
 * 
 * Functions:
 *   toLocalDateStr     - Extract YYYY-MM-DD without timezone shift
 *   resolveField       - Read camel or snake key from API/backend objects
 *   parseLocalDate     - Parse YYYY-MM-DD as local (no UTC shift)
 *   getTodayStr        - Today as YYYY-MM-DD in local time
 *   formatPace         - Seconds/km to "M:SS" pace string
 *   formatDuration     - A workout's length in minutes, as "N min"
 *   formatDurationFromSeconds - Seconds to "H:MM:SS" or "M:SS"
 *   sanitizeDateObjects - Deep-clean Date objects and empty {} from API data
 *   CHILD_MOBILITY_RE   - Regex to detect child mobility titles (warmup/cooldown/pre-run/post-run)
 */

// ─────────────────────────────────────────────
// toLocalDateStr
// ─────────────────────────────────────────────
/**
 * Extract a YYYY-MM-DD date string without timezone conversion.
 * 
 * Database stores dates as DATE type (YYYY-MM-DD, no timezone).
 * new Date("2026-01-05") in Pacific time → "2026-01-04" (WRONG).
 * This function prevents that shift by string-parsing first.
 *
 * Used by: MyPath, ThisWeeksPlanCard, day-dot visual, plan filtering
 */
export const toLocalDateStr = (dateInput: string | Date): string => {
  // If it's already a YYYY-MM-DD string (or starts with one), just extract it
  if (typeof dateInput === 'string' && dateInput.match(/^\d{4}-\d{2}-\d{2}/)) {
    return dateInput.substring(0, 10);
  }
  // Otherwise extract local date components (no UTC conversion)
  const d = dateInput instanceof Date ? dateInput : new Date(dateInput);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// ─────────────────────────────────────────────
// resolveField
// ─────────────────────────────────────────────
/**
 * Read a field from API/backend objects that may use camelCase or snake_case.
 * Used by plan/week card components to handle both frontend and raw API shapes.
 */
export function resolveField<T = unknown>(
  obj: Record<string, unknown> | null | undefined,
  camel: string,
  snake: string
): T | undefined {
  if (obj?.[camel] !== undefined) return obj[camel] as T;
  if (obj?.[snake] !== undefined) return obj[snake] as T;
  return undefined;
}

// ─────────────────────────────────────────────
// parseLocalDate
// ─────────────────────────────────────────────
/**
 * Parse a YYYY-MM-DD string as local date (no UTC shift).
 * new Date('2026-01-05') in Pacific → Jan 4 6pm UTC. This avoids that.
 */
export function parseLocalDate(dateStr: string): Date {
  if (!dateStr) return new Date();
  if (dateStr.length >= 10) {
    const [y, m, d] = dateStr.substring(0, 10).split('-').map(Number);
    if (!Number.isNaN(y) && !Number.isNaN(m) && !Number.isNaN(d)) {
      return new Date(y, m - 1, d);
    }
  }
  return new Date(dateStr);
}

// ─────────────────────────────────────────────
// getTodayStr
// ─────────────────────────────────────────────
/** Today as YYYY-MM-DD in local time. */
export function getTodayStr(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

// ─────────────────────────────────────────────
// isSessionReminderEligible
// ─────────────────────────────────────────────
/**
 * ⏰ Appointed-Time Grove: a per-session reminder may be set on any session
 * scheduled from TODAY through the next REMINDER_HORIZON_DAYS days
 * (user-local) — i.e. the current plan week, not just today/tomorrow. Bounded
 * to the week on purpose: the Sunday plan-regen archives sessions beyond it and
 * drops their pending reminders (no carry-forward until v2), so a 7-day ceiling
 * keeps the reminder firing before the regen that would clobber it. Past dates
 * are excluded. Mirrors the backend `reminder_horizon_days` writer guard
 * (must move together — a full rollback flips FF_SESSION_REMINDER_WEEK_HORIZON
 * AND OTA-reverts this horizon).
 *
 * Local-date comparison (device clock IS the user's tz, same basis the
 * dispatcher uses). Returns false for absent/unparseable/past dates.
 */
export const REMINDER_HORIZON_DAYS = 7; // keep in sync with services/session_reminder_flags.py

export function isSessionReminderEligible(scheduledDate: string | Date | undefined | null): boolean {
  if (!scheduledDate) return false;
  const sessionStr = toLocalDateStr(scheduledDate);
  const today = new Date();
  const todayStr = toLocalDateStr(today);
  if (sessionStr < todayStr) return false; // no reminders on past sessions
  const horizon = new Date(today);
  horizon.setDate(horizon.getDate() + REMINDER_HORIZON_DAYS);
  const horizonStr = toLocalDateStr(horizon);
  return sessionStr <= horizonStr;
}

// ─────────────────────────────────────────────
// getDisplayDayLabel
// ─────────────────────────────────────────────
/**
 * Format date as "Mon, Jan 5" (weekday short, month short, day).
 * Used by plan/week cards and mobility cards for scheduled date display.
 */
export function getDisplayDayLabel(d: Date | string | undefined): string {
  if (!d) return '';
  const date = typeof d === 'string' ? new Date(d) : d;
  if (isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Weekday only, e.g. "Mon". For day-dot labels and compact headers.
 */
export function getShortWeekday(d: Date): string {
  return d.toLocaleDateString('en-US', { weekday: 'short' });
}

// ─────────────────────────────────────────────
// getWeekRange
// ─────────────────────────────────────────────
/**
 * Return Monday–Sunday week containing the given date (local time).
 * start/end are YYYY-MM-DD. Used by ThisWeeksPlanCard and week filtering.
 */
export function getWeekRange(dateInput: Date | string): { start: string; end: string } {
  const d = typeof dateInput === 'string' ? parseLocalDate(dateInput) : dateInput;
  const day = d.getDay();
  const mon = new Date(d);
  mon.setDate(d.getDate() - (day === 0 ? 6 : day - 1));
  const sun = new Date(mon);
  sun.setDate(mon.getDate() + 6);
  return {
    start: toLocalDateStr(mon),
    end: toLocalDateStr(sun),
  };
}

// ─────────────────────────────────────────────
// isToday
// ─────────────────────────────────────────────
/** True if the given date is today in local time. */
export function isToday(dateInput: Date | string): boolean {
  const d = typeof dateInput === 'string' ? parseLocalDate(dateInput) : dateInput;
  const today = getTodayStr();
  const target = toLocalDateStr(d);
  return target === today;
}

// ─────────────────────────────────────────────
// formatPace
// ─────────────────────────────────────────────
// ─────────────────────────────────────────────
/**
 * Convert seconds-per-kilometer to a pace string "M:SS".
 * Returns '--:--' for invalid/infinite values.
 *
 * Used by: UnifiedWorkoutTimer, PostCompletionMetricsGrid, ExpandablePhaseBreakdown,
 *          ExpandableGPSRouteMap, ActivityDetailExplorer, WorkoutSessionResults,
 *          ExpandableSegments, ExpandableSplits, RunningTracker
 */
export const formatPace = (secondsPerKm: number): string => {
  if (!secondsPerKm || !isFinite(secondsPerKm) || secondsPerKm <= 0) return '--:--';
  const minutes = Math.floor(secondsPerKm / 60);
  const seconds = Math.floor(secondsPerKm % 60);
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

// ─────────────────────────────────────────────
// formatDuration
// ─────────────────────────────────────────────
/**
 * A workout's length in minutes, as the cards write it.
 *   < 1 min → ''
 *   else    → '45 min', '90 min', '180 min' (whole minutes; an hour or more is minutes too)
 */
export const formatDuration = (minutes: number | undefined | null): string => {
  if (minutes == null || minutes < 1) return '';
  return `${Math.round(minutes)} min`;
};

// ─────────────────────────────────────────────
// formatDurationFromSeconds
// ─────────────────────────────────────────────
/**
 * Convert total seconds to "H:MM:SS" or "M:SS" string.
 * Useful for timer displays and workout elapsed time.
 *
 * Used by: UnifiedWorkoutTimer, activity cards
 */
export const formatDurationFromSeconds = (totalSeconds: number): string => {
  if (!totalSeconds || totalSeconds < 0) return '0:00';
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = Math.floor(totalSeconds % 60);
  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

// ─────────────────────────────────────────────
// sanitizeDateObjects
// ─────────────────────────────────────────────
/**
 * Deep-clean an object tree, converting Date objects to ISO strings
 * and empty objects {} to undefined.
 * 
 * React Error #31 occurs when Date objects or empty {} are rendered
 * directly in JSX. This function prevents that.
 *
 * Used by: WorkoutDetail, any component receiving API data with Date fields
 */
export const sanitizeDateObjects = (obj: any): any => {
  if (obj === null || obj === undefined) return obj;
  if (typeof obj !== 'object') return obj;
  if (obj instanceof Date) return obj.toISOString();
  if (Array.isArray(obj)) return obj.map(sanitizeDateObjects);

  // Empty objects {} → undefined (prevents React Error #31)
  if (Object.keys(obj).length === 0) {
    return undefined;
  }

  const sanitized: any = {};
  for (const key in obj) {
    if (!Object.prototype.hasOwnProperty.call(obj, key)) continue;
    const value = obj[key];
    if (value instanceof Date) {
      sanitized[key] = value.toISOString();
    } else if (
      value &&
      typeof value === 'object' &&
      !Array.isArray(value) &&
      Object.keys(value).length === 0
    ) {
      sanitized[key] = undefined;
    } else if (typeof value === 'object') {
      sanitized[key] = sanitizeDateObjects(value);
    } else {
      sanitized[key] = value;
    }
  }
  return sanitized;
};

// ─────────────────────────────────────────────
// formatDistance
// ─────────────────────────────────────────────
/**
 * Format distance in meters to a human-readable string.
 *   < 1000m → '800m'
 *   ≥ 1000m → '5.2 km'
 */
export const formatDistance = (meters: number | undefined | null): string => {
  if (meters == null || meters <= 0) return '';
  if (meters < 1000) return `${Math.round(meters)}m`;
  return `${(meters / 1000).toFixed(1)} km`;
};

// ─────────────────────────────────────────────
// formatCalories
// ─────────────────────────────────────────────
/**
 * Format calorie count with appropriate precision.
 *   < 10    → '8 cal'
 *   < 1000  → '350 cal'
 *   ≥ 1000  → '1,200 cal'
 */
export const formatCalories = (calories: number | undefined | null): string => {
  if (calories == null || calories <= 0) return '';
  if (calories >= 1000) return `${calories.toLocaleString()} cal`;
  return `${Math.round(calories)} cal`;
};

// ─────────────────────────────────────────────
// formatHeartRate
// ─────────────────────────────────────────────
/**
 * Format heart rate value with 'bpm' suffix.
 */
export const formatHeartRate = (bpm: number | undefined | null): string => {
  if (bpm == null || bpm <= 0) return '';
  return `${Math.round(bpm)} bpm`;
};

// ─────────────────────────────────────────────
// CHILD_MOBILITY_RE
// ─────────────────────────────────────────────
/**
 * Regex to detect child-mobility routine titles.
 * Matches warmup, cooldown, pre-run/walk/ride, post-run/walk/ride,
 * and recovery stretches — these are always child routines of a parent
 * workout and should be filtered from standalone mobility counts.
 *
 * Usage: `CHILD_MOBILITY_RE.test(title.toLowerCase())`
 *
 * Used by: ThisWeeksPlanCard, PlanDetail (session filtering)
 */
export const CHILD_MOBILITY_RE =
  /warm[- _]?up|cool[- _]?down|pre[- _]?run|pre[- _]?walk|pre[- _]?ride|post[- _]?run|post[- _]?walk|post[- _]?ride|recovery\s*stretch/i;
